import { Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';

export interface WizardStepperStage<T extends string = string> {
  id: T;
  label: string;
}

interface WizardStepperProps<T extends string> {
  stages: ReadonlyArray<WizardStepperStage<T>>;
  /** Currently displayed stage id. */
  current: T;
  /**
   * The stage the wizard would land on based purely on server-observable
   * state. The user can revisit any step at or before whichever is later
   * (current or furthest); anything past stays disabled.
   */
  furthest: T;
  onSelect: (id: T) => void;
}

type StepState = 'current' | 'done' | 'reachable' | 'disabled';

/**
 * Compact dot-rail progress indicator for the setup wizard. Inspired by the
 * Stripe / Linear / Vercel onboarding pattern: the rail itself only carries
 * numbered/checked circles, while the current step's full label is rendered
 * prominently above so it never has to fit inside a narrow flex slot.
 *
 * Past, future, and reachable step labels are still discoverable — each dot
 * is a real button with a stable `aria-label` and a Tooltip that fires on
 * hover or focus.
 */
export function WizardStepper<T extends string>({
  stages,
  current,
  furthest,
  onSelect,
}: WizardStepperProps<T>) {
  const currentIdx = stages.findIndex((s) => s.id === current);
  const furthestIdx = stages.findIndex((s) => s.id === furthest);
  const reachableUpTo = Math.max(currentIdx, furthestIdx);
  const currentStage = stages[currentIdx];

  return (
    <nav aria-label="Setup wizard progress" data-testid="wizard-stepper" className="mb-8 space-y-4">
      <div className="flex items-baseline gap-2">
        {currentStage && (
          <PanelTitle
            as="h2"
            size="sm"
            className="text-base font-medium tracking-tight text-foreground"
          >
            <span data-testid="wizard-stepper-current-label">{currentStage.label}</span>
          </PanelTitle>
        )}
        <SectionLabel as="span" data-testid="wizard-stepper-counter">
          · Step {currentIdx + 1} of {stages.length}
        </SectionLabel>
      </div>
      <ol className="flex items-center">
        {stages.map((s, i) => {
          const state: StepState =
            i === currentIdx
              ? 'current'
              : i < currentIdx
                ? 'done'
                : i <= reachableUpTo && s.id !== 'done'
                  ? 'reachable'
                  : 'disabled';
          const isLast = i === stages.length - 1;
          const segmentDone = i < currentIdx;
          return (
            <li key={s.id} className={cn('flex items-center', !isLast && 'flex-1')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid={`wizard-stepper-step-${s.id}`}
                    data-state={state}
                    aria-label={`Go to step ${i + 1}: ${s.label}`}
                    aria-current={state === 'current' ? 'step' : undefined}
                    disabled={state === 'disabled'}
                    onClick={() => onSelect(s.id)}
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full border text-2xs font-medium tabular-nums outline-none transition-all',
                      'focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      // Current: solid primary fill plus a soft glow ring — the
                      // unmistakable focal point. In this dark theme `primary`
                      // is near-white, so the glow does the visual lifting.
                      state === 'current' &&
                        'border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px] shadow-primary/25',
                      // Done: muted neutral fill with a primary-colored check —
                      // visibly "completed" but clearly recedes behind the
                      // current step rather than competing with it.
                      state === 'done' && 'border-border bg-muted/80 text-primary hover:bg-muted',
                      // Reachable: hollow circle, muted text, brighter on hover.
                      state === 'reachable' &&
                        'border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                      // Disabled: very low contrast, clearly out of reach.
                      state === 'disabled' &&
                        'cursor-not-allowed border-border/40 bg-muted/20 text-muted-foreground/40',
                    )}
                  >
                    {state === 'done' ? <Check className="icon-xs" strokeWidth={3} /> : i + 1}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Step {i + 1}: {s.label}
                </TooltipContent>
              </Tooltip>
              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    'mx-1 h-px flex-1 transition-colors',
                    segmentDone ? 'bg-primary/50' : 'bg-border/40',
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
